import {
  ClerkProvider,
  useAuth as useClerkAuth,
  useClerk,
  useUser,
} from "@clerk/react";
import { ConvexProviderWithClerk } from "convex/react-clerk";
import { useMemo } from "react";
import type { ReactNode } from "react";
import type { ConvexReactClient } from "convex/react";

import { getClerkConfig } from "./config";
import { NkuliAuthContext } from "./nkuliAuthContext";
import type { NkuliAuthContextValue } from "./nkuliAuthContext";

export function NkuliAuthProvider({
  children,
  client,
}: {
  children: ReactNode;
  client: ConvexReactClient;
}) {
  const clerkConfig = getClerkConfig();

  return (
    <ClerkProvider
      afterSignOutUrl="/"
      publishableKey={clerkConfig.publishableKey}
    >
      <ConvexProviderWithClerk client={client} useAuth={useClerkAuth}>
        <ClerkAuthBridge>{children}</ClerkAuthBridge>
      </ConvexProviderWithClerk>
    </ClerkProvider>
  );
}

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { isLoaded: isUserLoaded, user } = useUser();
  const clerk = useClerk();
  const clerkLoaded = isLoaded && isUserLoaded;
  const value = useMemo<NkuliAuthContextValue>(
    () => ({
      providerLoading: !clerkLoaded,
      providerAuthenticated: Boolean(isSignedIn || user),
      signIn: () => clerk.openSignIn(),
      signUp: () => clerk.openSignUp(),
      signOut: () => clerk.signOut({ redirectUrl: "/" }),
    }),
    [clerk, clerkLoaded, isSignedIn, user],
  );

  return (
    <NkuliAuthContext.Provider value={value}>
      {children}
    </NkuliAuthContext.Provider>
  );
}
